import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfigService} from '@config/config.service';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {PageStreetDto} from '@services/associations/model/pageStreetDto';
import {StreetDto} from '@services/associations/model/streetDto';
import {ListDialogComponent} from '@shared/components/list-dialog/list-dialog.component';
import {ListItemType} from '@shared/enum/list-item-type';
import {AssociationsService} from '@shared/services/associations.service';
import {DictionariesService} from '@shared/services/dictionaries.service';
import {SharedService} from '@shared/shared.service';
import {ValidateIfObject} from '@shared/validators/if-object.validator';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, debounceTime, EMPTY, Subject, takeUntil} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {OrganizationBuildingModel} from '@services/back-office/model/organizationBuildingModel';
import {OrganizationSectionModel} from '@services/back-office/model/organizationSectionModel';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-building-dialog',
    templateUrl: './building-dialog.component.html',
    styleUrls: ['./building-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BuildingDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    buildingForm: UntypedFormGroup;
    entrancesForm: UntypedFormGroup;
    /** Address */
    streets$ = new BehaviorSubject<StreetDto[] | null | undefined>(null);
    sectionsChanged: boolean;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    /** Scrollbar */
    @ViewChild('scrollableContent') private scrollableContent: ElementRef;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            association: AssociationListResponse,
            building?: OrganizationBuildingModel,
            aptUnion?: boolean
        },
        public dialogRef: MatDialogRef<BuildingDialogComponent>,
        private associationsService: AssociationsService,
        private dictionariesService: DictionariesService,
        private formBuilder: UntypedFormBuilder,
        private sharedService: SharedService,
        private config: ConfigService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        this.initFormArray();
        /** Patch Form On Edit */
        if (this.data.building) {
            this.patchForm();
        }
        if (this.data.building?.sections?.length) {
            this.patchEntrancesForm(this.data.building.sections);
        }

        this.watchStreetInputChanged();
    }

    ngOnDestroy() {
        this.destroyed$.complete();
        this.loading$.complete();
        this.streets$.complete();
    }

    initForm() {
        this.buildingForm = this.formBuilder.group({
            aptUnion: [this.data.aptUnion],
            name: [''],
            street: ['', [ValidateIfObject]],
            number: ['']
        });

        this.handleConditionalValidators();
    }

    /** Form array */
    initFormArray() {
        this.entrancesForm = this.formBuilder.group({
            entrancesFormControls: this.formBuilder.array([this.initSetControls()])
        });
    }

    initSetControls() {
        return this.formBuilder.group({
            id: [''],
            entranceType: ['ENTRANCE', [Validators.required]],
            entranceNumber: ['', [Validators.required]],
            firstFloor: ['', [Validators.pattern(this.config.regex.digits)]],
            lastFloor: ['', [Validators.pattern(this.config.regex.digits)]],
        });
    }

    getControls() {
        return (this.entrancesForm.get('entrancesFormControls') as UntypedFormArray).controls;
    }

    getFormArrayControlsQuantity() {
        return (this.entrancesForm.get('entrancesFormControls') as UntypedFormArray).controls.length;
    }

    addFormArrayItem() {
        (this.entrancesForm.controls['entrancesFormControls'] as UntypedFormArray).push(this.initSetControls());
        setTimeout(() => {
            this.scrollToBottom();
        }, 50);

        const lastIndex = this.getFormArrayControlsQuantity() - 1;

        if (lastIndex < 1) {
            return;
        }

        this.getControls()[lastIndex].patchValue({
            entranceType: this.getControls()[lastIndex - 1].get('entranceType')?.value
                ? this.getControls()[lastIndex - 1].get('entranceType')?.value
                : 'ENTRANCE',
            entranceNumber: this.getControls()[lastIndex - 1].get('entranceNumber')?.value
                ? Number(this.getControls()[lastIndex - 1].get('entranceNumber')?.value) + 1
                : '',
            firstFloor: this.getControls()[lastIndex - 1].get('firstFloor')?.value
                ? this.getControls()[lastIndex - 1].get('firstFloor')?.value
                : '',
            lastFloor: this.getControls()[lastIndex - 1].get('lastFloor')?.value
                ? this.getControls()[lastIndex - 1].get('lastFloor')?.value
                : ''
        });
    }

    getEntranceId(key: any) {
        return this.getControls()[key].get('id')?.value;
    }

    deleteFormArrayItem(key: any) {
        if (this.getEntranceId(key)) {
            this.loading$.next(true);
            this.associationsService.deleteSection(this.getEntranceId(key)).pipe(
                take(1),
                map(() => {
                    (this.entrancesForm.controls['entrancesFormControls'] as UntypedFormArray).removeAt(key);
                    this.sectionsChanged = true;
                    this.loading$.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    this.loading$.next(false);
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    return EMPTY;
                })
            ).subscribe();
        } else {
            (this.entrancesForm.controls['entrancesFormControls'] as UntypedFormArray).removeAt(key);
        }
    }

    /** Patch Form On Edit */
    patchForm() {
        if (!this.buildingForm) {
            this.initForm();
        }
        this.buildingForm.patchValue({
            aptUnion: !!this.data.building?.aptUnion,
            ...this.data.building?.name && {name: this.data.building?.name},
            ...this.data.building?.street && {street: this.data.building?.street},
            ...this.data.building?.number && {number: this.data.building?.number},
        });
    }

    patchEntrancesForm(entrances: OrganizationSectionModel[]) {
        this.initSetControls();

        const difference = entrances.length - this.getFormArrayControlsQuantity();

        if (difference > 0) {
            for (let i = 0; i < difference; i++) {
                (this.entrancesForm.controls['entrancesFormControls'] as UntypedFormArray).push(this.initSetControls());
            }
        }

        for (const i in entrances) {
            const entrance: OrganizationSectionModel = entrances[i];
            const controls = this.getControls();
            if (controls[i]) {
                controls[i].get('id')!.setValue(entrance.id);
                controls[i].get('entranceType')!.setValue(entrance.type);
                controls[i].get('entranceNumber')!.setValue(entrance.number);
                controls[i].get('firstFloor')!.setValue(entrance.firstFloor != undefined ? entrance.firstFloor : '');
                controls[i].get('lastFloor')!.setValue(entrance.lastFloor != undefined ? entrance.lastFloor : '');
            }
        }
    }

    /** Street */
    watchStreetInputChanged() {
        if (!this.buildingForm) {
            this.initForm();
        }

        this.buildingForm.controls['street'].valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(this.config.searchTimeout)
        ).subscribe(selectedValue => {
            if (typeof selectedValue == 'string' && selectedValue?.trim() || (this.buildingForm?.get('street')?.value?.length && !selectedValue?.length)) {
                this.getStreets(selectedValue);
            }
        });
    }

    getStreets(streetName: string) {
        this.dictionariesService.getStreets({name: streetName, activeOnly: true, cityId: this.data.association.city?.id}).pipe(
            takeUntil(this.destroyed$),
            map((response: PageStreetDto) => {
                this.streets$.next(response.content);
            }),
            catchError(() => {
                return EMPTY;
            })
        ).subscribe();
    }

    openListDialog() {
        const listDialog$ = this.sharedService.openDialog(
            ListDialogComponent,
            {...this.config.itemsListDialog, hasBackdrop: true},
            {
                itemsType: ListItemType.STREET,
                props: {
                    cityId: this.data.association.city?.id
                }
            }
        );

        listDialog$.afterClosed().pipe(
            take(1),
        ).subscribe(dialogData => {
            if (dialogData.result && dialogData.data) {
                this.buildingForm.get('street')?.setValue({
                    id: dialogData.data.id,
                    name: dialogData.data.name
                });
            }
        });
    }

    displayStreetFn(street: StreetDto): string {
        return street ? street.name! : '';
    }

    streetIsSelected() {
        this.streets$.next(null);
    }

    getRequest() {
        const formData = this.buildingForm.getRawValue();
        const entrancesFormData = this.entrancesForm.getRawValue();

        const entrances: OrganizationSectionModel[] =
            entrancesFormData &&
            entrancesFormData.entrancesFormControls &&
            entrancesFormData.entrancesFormControls.length ?
                entrancesFormData.entrancesFormControls.map((item: any) => {
                    return {
                        ...item.id && {id: item.id},
                        type: item.entranceType,
                        number: item.entranceNumber,
                        ...item.firstFloor !== null && item.firstFloor !== undefined && {firstFloor: item.firstFloor},
                        ...item.lastFloor !== null && item.lastFloor !== undefined && {lastFloor: item.lastFloor},
                    };
                }) : [];

        const request: OrganizationBuildingModel = {
            ...this.data.association && {id: this.data.association.id, name: this.data.association.name},
            aptUnion: !!formData.aptUnion,
            ...formData.aptUnion && formData.name && {name: formData.name},
            ...!formData.aptUnion && formData.number && {number: formData.number},
            ...!formData.aptUnion && formData.street && {street: {id: formData.street.id, name: formData.street.name}},
            ...!formData.aptUnion && {sections: entrances}
        };

        return request;
    }

    submitForm() {
        if (this.data.building) {
            this.editBuilding(this.getRequest());
        } else {
            this.addBuilding(this.getRequest());
        }
    }

    /** Add Building */
    addBuilding(request: OrganizationBuildingModel) {
        this.loading$.next(true);
        this.associationsService.addBuilding(this.data.association.id!, request).pipe(
            take(1),
            map((response: OrganizationBuildingModel) => {
                this.loading$.next(false);
                this.closeDialog(true, response);
            }),
            catchError((error: HttpErrorResponse) => {
                this.loading$.next(false);
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            })
        ).subscribe();
    }

    /** Edit Building */
    editBuilding(request: OrganizationBuildingModel) {
        this.loading$.next(true);
        this.associationsService.editBuilding(this.data.building!.id!, request).pipe(
            take(1),
            map((response: OrganizationBuildingModel) => {
                this.loading$.next(false);
                this.closeDialog(true, response);
            }),
            catchError((error: HttpErrorResponse) => {
                this.loading$.next(false);
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            })
        ).subscribe();
    }

    scrollToBottom() {
        this.scrollableContent.nativeElement.scrollTop = this.scrollableContent.nativeElement.scrollHeight;
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        if (this.sectionsChanged && !result) {
            this.dialogRef.close({
                result: true,
                data: {
                    ...this.data.building,
                    ...this.getRequest()
                }
            });
        } else {
            this.dialogRef.close({result, data});
        }
    }

    private handleConditionalValidators(): void {
        const aptUnionControl = this.buildingForm.get('aptUnion');
        const nameControl = this.buildingForm.get('name');
        const streetControl = this.buildingForm.get('street');
        const numberControl = this.buildingForm.get('number');

        aptUnionControl!.valueChanges.subscribe((isAptUnion: boolean) => {
            if (isAptUnion) {
                nameControl!.setValidators([Validators.required]);

                streetControl!.clearValidators();
                numberControl!.clearValidators();
            } else {
                streetControl!.setValidators([Validators.required, ValidateIfObject]);
                numberControl!.setValidators([Validators.required]);

                nameControl!.clearValidators();
            }

            nameControl!.updateValueAndValidity();
            streetControl!.updateValueAndValidity();
            numberControl!.updateValueAndValidity();
        });

        aptUnionControl!.updateValueAndValidity({emitEvent: true});
    }
}
